/**
 * Альфа-каталог апгрейдов для эпохи 1-2.
 *
 * Source of truth: `docs/14_upgrades_system.md` §«Каталог апгрейдов».
 *
 * Принцип #9 (CLAUDE.md): **каждый апгрейд = видимое изменение мира**.
 * Поэтому `visibleChange` обязателен — CSS-переменная на корне или
 * componentFlag для рендереров Искры и сцены.
 *
 * JB 2026-05-25: апгрейды должны быть разнонаправленные, не только
 * множитель. Поэтому помимо tap multipliers есть:
 *   - U003 cooldownReduction (Искра готова быстрее)
 *   - U021 rhythmWindowExtension (легче держать стрик)
 *   - U040 crystallizationBonus (больше духобаллов с кристаллизации)
 *
 * Каждый апгрейд первого уровня также автоматически разблокирует
 * milestone-артефакт через триггер `upgradePurchasedExactly` — см.
 * `apps/web/src/visual-artifacts/catalog.ts`.
 */
import type { Upgrade } from '@sai-clicker/game-core';

export const UPGRADES_ALPHA: readonly Upgrade[] = [
  // ============================================================
  // ---- Малые (за осознания) ----
  // ============================================================

  // ---- Tap multipliers — больше осознаний с каждого тапа ----
  {
    id: 'u001_bright_spark',
    name: 'Светлая Искра',
    description: 'Свет внутри собирается. Каждый тап приносит чуть больше осознаний.',
    category: 'tap',
    currency: 'awarenesses',
    baseCost: 10,
    costGrowthRate: 1.15,
    maxLevel: 50,
    epoch: 1,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      multiplierPerLevel: 0.05, // +5% per level
    },
    visibleChange: {
      description: 'Свечение Искры глубже с каждым уровнем.',
      cssVariable: '--spark-extra-glow',
      cssValueFn: (level: number) => Math.min(1, level / 50).toString(),
      soundEffect: 'small_upgrade_purchased',
    },
  },
  {
    id: 'u002_deep_tap',
    name: 'Глубокий тап',
    description: 'Ядро Искры плотнее. Тап приносит ощутимо больше осознаний.',
    category: 'tap',
    currency: 'awarenesses',
    baseCost: 50,
    costGrowthRate: 1.18,
    maxLevel: 30,
    epoch: 1,
    requires: { upgradeId: 'u001_bright_spark', minLevel: 5 },
    effect: {
      kind: 'multiplier',
      target: 'tap',
      multiplierPerLevel: 0.1, // +10% per level
    },
    visibleChange: {
      description: 'Ядро Искры уплотняется и тяжелеет светом.',
      cssVariable: '--spark-core-density',
      cssValueFn: (level: number) => Math.min(1, level / 30).toString(),
      soundEffect: 'small_upgrade_purchased',
    },
  },
  {
    id: 'u010_voice_of_sense',
    name: 'Голос ощущения',
    description: 'Чувства слышат друг друга. Тап звучит для тела.',
    category: 'tap',
    currency: 'awarenesses',
    baseCost: 30,
    costGrowthRate: 1.16,
    maxLevel: 30,
    epoch: 1,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      multiplierPerLevel: 0.08, // +8% per level
    },
    visibleChange: {
      description: 'Вокруг Искры расцветает мягкое зелёное сияние.',
      cssVariable: '--sensory-aura',
      cssValueFn: (level: number) => Math.min(0.5, level / 60).toString(),
      soundEffect: 'small_upgrade_purchased',
    },
  },
  {
    id: 'u030_winged_atlas',
    name: 'Атлас крылатый',
    description: 'Каждая собранная фраза помнится сильнее. Все тапы становятся весомее.',
    category: 'memory',
    currency: 'awarenesses',
    baseCost: 50,
    costGrowthRate: 1.15,
    maxLevel: 40,
    epoch: 1,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      multiplierPerLevel: 0.04, // +4% per level
    },
    visibleChange: {
      description: 'Собранные фразы в Атласе светятся ярче.',
      componentFlag: 'atlas-glow-active',
      soundEffect: 'small_upgrade_purchased',
    },
  },

  // ---- Cooldown reduction — Искра готова быстрее ----
  {
    id: 'u003_finger_resonance',
    name: 'Резонанс пальца',
    description: 'Тело узнаёт ритм. Искра отвечает на тап быстрее.',
    category: 'rhythm',
    currency: 'awarenesses',
    baseCost: 80,
    costGrowthRate: 1.22,
    maxLevel: 20,
    epoch: 1,
    requires: { upgradeId: 'u001_bright_spark', minLevel: 3 },
    effect: {
      kind: 'cooldownReduction',
      msPerLevel: 50, // 20 уровней × 50ms = 1000ms убирает (с base 1500ms → MIN_TAP_COOLDOWN_MS пол)
    },
    visibleChange: {
      description: 'Кольцо кулдауна Искры заполняется быстрее.',
      cssVariable: '--cooldown-pace',
      cssValueFn: (level: number) => (1 - Math.min(0.7, level / 30)).toString(),
      soundEffect: 'small_upgrade_purchased',
    },
  },

  // ---- Rhythm window — легче держать стрик ----
  {
    id: 'u021_purity_chain',
    name: 'Серия чистоты',
    description: 'Ритм держит связь между тапами. Окно стрика становится шире.',
    category: 'rhythm',
    currency: 'awarenesses',
    baseCost: 120,
    costGrowthRate: 1.2,
    maxLevel: 15,
    epoch: 1,
    requires: { upgradeId: 'u001_bright_spark', minLevel: 5 },
    effect: {
      kind: 'rhythmWindowExtension',
      msPerLevel: 60, // 15 уровней × 60ms = +900ms к окну
    },
    visibleChange: {
      description: 'Между тапами в стрике натягивается тонкая золотая нить.',
      componentFlag: 'streak-thread-visible',
      soundEffect: 'small_upgrade_purchased',
    },
  },

  // ============================================================
  // ---- Большие (за духобаллы) ----
  // ============================================================

  // ---- Crystallization bonus — кристалл даёт больше ----
  {
    id: 'u040_crystal_alchemy',
    name: 'Алхимия кристалла',
    description: 'Каждая кристаллизация переплавляет осознания в больше духобаллов.',
    category: 'memory',
    currency: 'spirit_balls',
    baseCost: 2,
    costGrowthRate: 1.6,
    maxLevel: 8,
    epoch: 1,
    effect: {
      kind: 'crystallizationBonus',
      perLevel: 0.15, // 8 уровней × 15% = ×2.2 итог
    },
    visibleChange: {
      description: 'Анимация кристаллизации становится плотнее, длиннее.',
      componentFlag: 'crystal-rich-animation',
      soundEffect: 'big_upgrade_purchased',
    },
  },

  // ---- Guide — первый Проводник ----
  {
    id: 'b001_silence_keeper',
    name: 'Хранитель тишины',
    description: 'Первый Проводник приходит в Рощу. Тёплая точка справа от Искры — присутствие.',
    category: 'guide',
    currency: 'spirit_balls',
    baseCost: 5,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 1,
    effect: {
      kind: 'guide',
      guideId: 'silence_keeper',
    },
    visibleChange: {
      description: 'Хранитель тишины появляется как тёплая точка справа от Искры.',
      componentFlag: 'guide-silence-keeper-present',
      soundEffect: 'big_upgrade_purchased',
    },
  },

  // ============================================================
  // ---- ЭПОХА 2 — Фрактальный метод (E minor) ----
  // ============================================================

  // ---- B002 Хранитель тишины ур.2 — апгрейд первого Проводника ----
  // JB 2026-05-26 mechanics audit fix: было `target: 'passive'` который
  // нигде не применяется (механика B не имплементирована). Переключено на
  // tap multiplier +25% — это даёт осязаемый эффект сразу: Хранитель
  // делится своим вниманием с каждым твоим тапом.
  {
    id: 'b002_silence_keeper_2',
    name: 'Хранитель — глубже',
    description:
      'Хранитель тишины обретает второй уровень. Он делится вниманием — каждый тап весомее.',
    category: 'guide',
    currency: 'spirit_balls',
    baseCost: 12,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 2,
    requires: { upgradeId: 'b001_silence_keeper', minLevel: 1 },
    effect: {
      kind: 'multiplier',
      target: 'tap',
      multiplierPerLevel: 0.25, // +25% к тапу — это «второй уровень» Проводника
    },
    visibleChange: {
      description: 'Хранитель тишины пульсирует мягче и заметнее.',
      componentFlag: 'guide-silence-keeper-tier2',
      soundEffect: 'big_upgrade_purchased',
    },
  },

  // ---- B003 Алхимик — второй Проводник ----
  // JB 2026-05-26 audit fix: было `effect: { kind: 'guide' }` — это
  // visual-only (рендерит компаньон), без mechanical эффекта. Описание
  // обещало «каждая кристаллизация даёт больше духобаллов», но
  // crystallizationBonus не применялся. Игрок тратил 15 духобаллов и
  // получал только зелёную точку. Теперь — реальный +25% bonus, флаг
  // визуала остаётся через `componentFlag` в visibleChange.
  {
    id: 'b003_alchemist',
    name: 'Алхимик',
    description: 'Второй Проводник. Каждая кристаллизация даёт +25% духобаллов.',
    category: 'guide',
    currency: 'spirit_balls',
    baseCost: 15,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 2,
    effect: {
      kind: 'crystallizationBonus',
      perLevel: 0.25,
    },
    visibleChange: {
      description: 'Алхимик появляется слева от Искры — зелёная точка-присутствие.',
      componentFlag: 'guide-alchemist-present',
      soundEffect: 'big_upgrade_purchased',
    },
  },

  // ---- U011 Корень слова — body layer multiplier ----
  {
    id: 'u011_word_root',
    name: 'Корень слова',
    description: 'Слово укореняется в теле. Body-фразы дают больше осознаний.',
    category: 'tap',
    currency: 'awarenesses',
    baseCost: 200,
    costGrowthRate: 1.18,
    maxLevel: 25,
    epoch: 2,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      layer: 'body',
      multiplierPerLevel: 0.1,
    },
    visibleChange: {
      description: 'Под Искрой появляется тёплое коричневое свечение — земля.',
      cssVariable: '--body-aura',
      cssValueFn: (level: number) => Math.min(0.5, level / 50).toString(),
      soundEffect: 'small_upgrade_purchased',
    },
  },

  // ---- U012 Глубинное осознание — awareness layer multiplier ----
  {
    id: 'u012_deep_awareness',
    name: 'Глубинное осознание',
    description: 'Awareness-фразы (особые milestone-вспышки) звучат громче.',
    category: 'tap',
    currency: 'awarenesses',
    baseCost: 500,
    costGrowthRate: 1.2,
    maxLevel: 20,
    epoch: 2,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      layer: 'awareness',
      multiplierPerLevel: 0.15,
    },
    visibleChange: {
      description: 'Awareness-фразы загораются ярче при появлении.',
      cssVariable: '--awareness-glow',
      cssValueFn: (level: number) => Math.min(1, level / 40).toString(),
      soundEffect: 'small_upgrade_purchased',
    },
  },

  // ---- B010 Беседка Фрактал — постройка эпохи 2 ----
  // JB 2026-05-26 audit fix: было `effect: { kind: 'building' }` — это
  // visual-only flag без mechanical эффекта. Игрок платил 25 духобаллов
  // и получал только componentFlag (Роща ещё не имплементирована).
  // Теперь — большой одноразовый +50% к тапу. Это согласуется с лором
  // («беседка для разбора смысла»: каждый тап становится в полтора раза
  // ёмче) и даёт ощутимый payoff за 25 духобаллов.
  {
    id: 'b010_fractal_pavilion',
    name: 'Беседка Фрактал',
    description: 'Место для разбора смысла. Каждый тап становится в полтора раза ёмче (+50%).',
    category: 'building',
    currency: 'spirit_balls',
    baseCost: 25,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 2,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      multiplierPerLevel: 0.5,
    },
    visibleChange: {
      description: 'Подсветка фрактала внутри Искры становится постоянной — беседка стоит.',
      componentFlag: 'building-fractal-pavilion',
      soundEffect: 'big_upgrade_purchased',
    },
  },

  // ============================================================
  // ---- ЭПОХА 3 — ФРАКТАЛiТи (по тональной системе — B minor) ----
  // ============================================================
  // JB director audit (doc 26 §1): hook map гэп «Недели 2-3 — эпоха 4-5».
  // Эпоха 3 — переход к ФРАКТАЛiТи (синтез фрактального метода). 5
  // апгрейдов baseline: 3 small (за осознания) + 2 big (за духобаллы).

  // ---- U060 Концентрация — общий tap multiplier ----
  {
    id: 'u060_concentration',
    name: 'Концентрация',
    description: 'Внимание собирается в одной точке. Каждый тап звучит для тела сильнее.',
    category: 'tap',
    currency: 'awarenesses',
    baseCost: 800,
    costGrowthRate: 1.2,
    maxLevel: 20,
    epoch: 3,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      multiplierPerLevel: 0.12,
    },
    visibleChange: {
      description: 'Ядро Искры становится плотнее с каждым уровнем (комбинируется с u002).',
      cssVariable: '--spark-core-density',
      cssValueFn: (level: number) => Math.min(1, level / 40).toString(),
      soundEffect: 'small_upgrade_purchased',
    },
  },

  // ---- U061 Долгое окно — расширение rhythm window ----
  {
    id: 'u061_long_window',
    name: 'Долгое окно',
    description: 'Ритм держит связь дольше. Между тапами в стрике больше времени.',
    category: 'rhythm',
    currency: 'awarenesses',
    baseCost: 1200,
    costGrowthRate: 1.22,
    maxLevel: 15,
    epoch: 3,
    requires: { upgradeId: 'u021_purity_chain', minLevel: 5 },
    effect: {
      kind: 'rhythmWindowExtension',
      msPerLevel: 80,
    },
    visibleChange: {
      description: 'Золотая нить между тапами становится толще и держится дольше.',
      componentFlag: 'streak-thread-visible',
      soundEffect: 'small_upgrade_purchased',
    },
  },

  // ---- U062 Тёплое возвращение — passive bonus при returns ----
  // Используем crystallizationBonus как proxy для общего «глубинного»
  // эффекта (mechanic A enhance via crystallization).
  {
    id: 'u062_warm_return',
    name: 'Тёплое возвращение',
    description: 'Кристаллизация после паузы — глубже. Память сильнее с каждым новым уровнем.',
    category: 'memory',
    currency: 'awarenesses',
    baseCost: 1500,
    costGrowthRate: 1.25,
    maxLevel: 12,
    epoch: 3,
    effect: {
      kind: 'crystallizationBonus',
      perLevel: 0.08,
    },
    visibleChange: {
      description: 'Анимация кристаллизации длится дольше и заканчивается ярче.',
      componentFlag: 'crystal-rich-animation',
      soundEffect: 'small_upgrade_purchased',
    },
  },

  // ---- B020 Третий Проводник — Садовник ----
  {
    id: 'b020_gardener',
    name: 'Садовник',
    description: 'Третий Проводник. Каждый тап даёт +35% — он заботится о всходах с тобой.',
    category: 'guide',
    currency: 'spirit_balls',
    baseCost: 25,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 3,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      multiplierPerLevel: 0.35,
    },
    visibleChange: {
      description: 'Садовник появляется снизу-справа — землисто-зелёная точка-присутствие.',
      componentFlag: 'guide-gardener-present',
      soundEffect: 'big_upgrade_purchased',
    },
  },

  // ============================================================
  // ---- Doc 27 — gameplay variety (новые kinds) ----
  // ============================================================
  // JB фидбек 2026-05-26: «у нас скучные апгрейды т.к. почти все
  // направлено тупо на процент коэффициента». 3 новых kind'а для
  // разнообразия gameplay (autoTap / streakBurst / passiveAwareness).

  // ---- B011 Помощник тишины (autoTap) — эпоха 2 ----
  // Cookie Clicker-style passive — Проводник касается Искры за тебя
  // каждые 6 секунд. Один уровень — не растёт. Лорно: «он держит
  // ритм пока ты в покое».
  {
    id: 'b011_silent_helper',
    name: 'Помощник тишины',
    description: 'Проводник тишины касается Искры каждые 6 секунд пока ты в покое.',
    category: 'guide',
    currency: 'spirit_balls',
    baseCost: 30,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 2,
    requires: { upgradeId: 'b001_silence_keeper', minLevel: 1 },
    effect: {
      kind: 'autoTap',
      intervalMsAtLevel1: 6000,
      intervalMsPerLevel: 0, // одноуровневый
    },
    visibleChange: {
      description: 'Каждые 6 секунд вокруг Искры — короткая пульсация автоматического касания.',
      componentFlag: 'guide-silent-helper-active',
      soundEffect: 'big_upgrade_purchased',
    },
  },

  // ---- U022 Каскад (streakBurst) — эпоха 2 ----
  // Каждый 10-й тап в стрике даёт ×3 (на ур.1, растёт до ×4.8 на ур.10).
  // Поощряет дисциплину игры — игроку приятно «дотянуть» до 10-го.
  {
    id: 'u022_cascade',
    name: 'Каскад',
    description:
      'Каждый 10-й тап в стрике даёт ×3 к награде. Ритм собирает энергию и выплёскивает её.',
    category: 'rhythm',
    currency: 'awarenesses',
    baseCost: 500,
    costGrowthRate: 1.22,
    maxLevel: 10,
    epoch: 2,
    requires: { upgradeId: 'u021_purity_chain', minLevel: 3 },
    effect: {
      kind: 'streakBurst',
      everyNth: 10,
      baseMultiplier: 3,
      multiplierPerLevel: 0.2,
    },
    visibleChange: {
      description: 'На каждом 10-м тапе в стрике — короткая золотая вспышка.',
      componentFlag: 'streak-burst-flash',
      soundEffect: 'small_upgrade_purchased',
    },
  },

  // ---- U025 Тихое дыхание (passiveAwareness) — эпоха 3 ----
  // Пассивно начисляет осознания пока игра в фокусе (или offline через
  // delta). 1 ос./сек на ур.1, +0.3 за уровень → 5.2 на ур.15.
  {
    id: 'u025_quiet_breath',
    name: 'Тихое дыхание',
    description: 'Искра дышит вместе с тобой. Приносит +1 осознание/сек пассивно.',
    category: 'memory',
    currency: 'awarenesses',
    baseCost: 800,
    costGrowthRate: 1.25,
    maxLevel: 15,
    epoch: 3,
    effect: {
      kind: 'passiveAwareness',
      perSecAtLevel1: 1,
      perSecPerLevel: 0.3,
    },
    visibleChange: {
      description: 'Рядом со счётчиком осознаний — мягкая пульсация «дышит +N/сек».',
      componentFlag: 'passive-breath-active',
      soundEffect: 'small_upgrade_purchased',
    },
  },

  // ---- B021 Большое тело — большой одноразовый bonus к layer ----
  {
    id: 'b021_great_body',
    name: 'Большое тело',
    description:
      'Body-фразы (телесные осознания) удваиваются. Земля под ногами становится плотной.',
    category: 'memory',
    currency: 'spirit_balls',
    baseCost: 40,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 3,
    requires: { upgradeId: 'u011_word_root', minLevel: 5 },
    effect: {
      kind: 'multiplier',
      target: 'tap',
      layer: 'body',
      multiplierPerLevel: 1.0, // +100% разово (×2 к body)
    },
    visibleChange: {
      description: 'Body-aura под Искрой становится в два раза плотнее.',
      cssVariable: '--body-aura',
      cssValueFn: (_level: number) => '0.5', // capped — additive с u011 уже даёт максимум
      soundEffect: 'big_upgrade_purchased',
    },
  },

  // ============================================================
  // ---- ЭПОХА 4 — МОГА (авторская методология) — F#m ----
  // ============================================================
  // Тема: ученик становится носителем метода. «Через тебя идёт свет».
  // Layer-multipliers + новый Проводник (b030 Учитель).

  {
    id: 'u070_clear_form',
    name: 'Ясная форма',
    description: 'Каждый тап несёт форму метода в себе. Sensory-фразы дают больше.',
    category: 'tap',
    currency: 'awarenesses',
    baseCost: 2000,
    costGrowthRate: 1.22,
    maxLevel: 20,
    epoch: 4,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      layer: 'sensory',
      multiplierPerLevel: 0.12,
    },
    visibleChange: {
      description: 'Sensory-фразы при появлении мерцают тоньше.',
      cssVariable: '--sensory-aura',
      cssValueFn: (level: number) => Math.min(0.7, level / 30).toString(),
      soundEffect: 'small_upgrade_purchased',
    },
  },

  {
    id: 'u071_voice_carrier',
    name: 'Голос несущий',
    description: 'Внутренний голос держит ритм между тапами. Окно стрика +100ms за уровень.',
    category: 'rhythm',
    currency: 'awarenesses',
    baseCost: 3000,
    costGrowthRate: 1.25,
    maxLevel: 12,
    epoch: 4,
    requires: { upgradeId: 'u021_purity_chain', minLevel: 8 },
    effect: {
      kind: 'rhythmWindowExtension',
      msPerLevel: 100,
    },
    visibleChange: {
      description: 'Золотая нить между тапами стала уверенней.',
      componentFlag: 'streak-thread-visible',
      soundEffect: 'small_upgrade_purchased',
    },
  },

  {
    id: 'u072_method_breath',
    name: 'Дыхание метода',
    description: 'Пассивно начисляет +2 осознания/сек. МОГА работает пока ты в покое.',
    category: 'memory',
    currency: 'awarenesses',
    baseCost: 4500,
    costGrowthRate: 1.28,
    maxLevel: 12,
    epoch: 4,
    requires: { upgradeId: 'u025_quiet_breath', minLevel: 5 },
    effect: {
      kind: 'passiveAwareness',
      perSecAtLevel1: 2,
      perSecPerLevel: 0.5,
    },
    visibleChange: {
      description: 'Над Искрой пульсирует мягкое поле — метод дышит вместе с тобой.',
      componentFlag: 'passive-breath-active',
      soundEffect: 'small_upgrade_purchased',
    },
  },

  {
    id: 'b030_teacher',
    name: 'Учитель',
    description: 'Четвёртый Проводник. Учитель тренирует тебя. Каждый тап +40%.',
    category: 'guide',
    currency: 'spirit_balls',
    baseCost: 50,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 4,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      multiplierPerLevel: 0.4,
    },
    visibleChange: {
      description: 'Учитель появляется сверху-слева — высветленная точка-присутствие.',
      componentFlag: 'guide-teacher-present',
      soundEffect: 'big_upgrade_purchased',
    },
  },

  {
    id: 'b031_method_pavilion',
    name: 'Беседка Метода',
    description: 'Место передачи метода. Кристаллизация даёт +50% духобаллов.',
    category: 'building',
    currency: 'spirit_balls',
    baseCost: 60,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 4,
    requires: { upgradeId: 'b010_fractal_pavilion', minLevel: 1 },
    effect: {
      kind: 'crystallizationBonus',
      perLevel: 0.5,
    },
    visibleChange: {
      description: 'Беседка обретает второй контур — теперь это место передачи.',
      componentFlag: 'building-method-pavilion',
      soundEffect: 'big_upgrade_purchased',
    },
  },

  // ============================================================
  // ---- ЭПОХА 5 — Sai Land Heal — C#m ----
  // ============================================================
  // Тема: земля как партнёр. Корни, мох, родник. «Каждый шаг лечит».

  {
    id: 'u080_root_depth',
    name: 'Глубина корней',
    description: 'Body-фразы (телесные осознания) удваиваются ещё сильнее с каждым уровнем.',
    category: 'tap',
    currency: 'awarenesses',
    baseCost: 6000,
    costGrowthRate: 1.25,
    maxLevel: 20,
    epoch: 5,
    requires: { upgradeId: 'u011_word_root', minLevel: 10 },
    effect: {
      kind: 'multiplier',
      target: 'tap',
      layer: 'body',
      multiplierPerLevel: 0.15,
    },
    visibleChange: {
      description: 'Body-aura под Искрой стала плотной как почва.',
      cssVariable: '--body-aura',
      cssValueFn: (level: number) => Math.min(0.7, level / 40 + 0.2).toString(),
      soundEffect: 'small_upgrade_purchased',
    },
  },

  {
    id: 'u081_morning_dew',
    name: 'Утренняя роса',
    description: 'Возврат после паузы — глубже. Каждый rejoin даёт временный +30% к тапам.',
    category: 'memory',
    currency: 'awarenesses',
    baseCost: 8000,
    costGrowthRate: 1.3,
    maxLevel: 10,
    epoch: 5,
    effect: {
      kind: 'crystallizationBonus',
      perLevel: 0.12,
    },
    visibleChange: {
      description: 'Анимация кристаллизации длится дольше с водяным мерцанием.',
      componentFlag: 'crystal-rich-animation',
      soundEffect: 'small_upgrade_purchased',
    },
  },

  {
    id: 'u082_grove_silence',
    name: 'Тишина Рощи',
    description: 'Тишина между тапами становится шире. Окно стрика +120ms за уровень.',
    category: 'rhythm',
    currency: 'awarenesses',
    baseCost: 10000,
    costGrowthRate: 1.3,
    maxLevel: 15,
    epoch: 5,
    requires: { upgradeId: 'u071_voice_carrier', minLevel: 4 },
    effect: {
      kind: 'rhythmWindowExtension',
      msPerLevel: 120,
    },
    visibleChange: {
      description: 'Между тапами стало просторнее — Роща дышит шире.',
      componentFlag: 'streak-thread-visible',
      soundEffect: 'small_upgrade_purchased',
    },
  },

  {
    id: 'b040_land_steward',
    name: 'Хранитель Земли',
    description: 'Пятый Проводник. Хранит место. +50% к тапу + passive 4 ос./сек.',
    category: 'guide',
    currency: 'spirit_balls',
    baseCost: 80,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 5,
    effect: {
      kind: 'multiplier',
      target: 'tap',
      multiplierPerLevel: 0.5,
    },
    visibleChange: {
      description: 'Хранитель Земли появляется снизу-слева — землисто-коричневая точка.',
      componentFlag: 'guide-land-steward-present',
      soundEffect: 'big_upgrade_purchased',
    },
  },

  {
    id: 'b041_grove_garden',
    name: 'Сад Рощи',
    description: 'Сад между деревьями. Кристаллизация даёт +75% духобаллов.',
    category: 'building',
    currency: 'spirit_balls',
    baseCost: 100,
    costGrowthRate: 1.5,
    maxLevel: 1,
    epoch: 5,
    requires: { upgradeId: 'b031_method_pavilion', minLevel: 1 },
    effect: {
      kind: 'crystallizationBonus',
      perLevel: 0.75,
    },
    visibleChange: {
      description: 'Вокруг Беседки появляется живой сад — мох, родник, цветы.',
      componentFlag: 'building-grove-garden',
      soundEffect: 'big_upgrade_purchased',
    },
  },
];

/** Quick lookup. */
export const UPGRADES_BY_ID: ReadonlyMap<string, Upgrade> = new Map(
  UPGRADES_ALPHA.map((u) => [u.id, u]),
);
